import {
    Box,
    Container,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Typography,
    DialogActions,
    Button,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { ZiggeoPlayer } from 'react-ziggeo';
import { dateTimeFormat, ZIGGEO_API_KEY } from '../../config/common.constants';
import { deleteVideoMail, getAllVideoMail } from '../../services/video-mail.service';
import { VideoMail } from '../../types';
import PageTitle from '../common/PageTitle';
import SEO from '../common/SEO';
import {
    SITE_DESCRIPTION,
    VIDEO_MAIL_PAGE_TITLE,
    VIDEO_MAIL_PAGE_DESCRIPTION_PREFIX,
} from '../../config/seo.constants';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '../common/Table';
import UserLayout from '../UserLayout';
import withAuth from '../../hoc/withAuth';
import useCustomerCallRedirect from '../../hooks/useCustomerCallRedirect';
import { useCall } from '../../customer-call-sdk';
import CloseIcon from '../common/Icons/Close';
import TextButton from '../common/TextButton';
import VideomailDialog from './VideomailDialog';
import { useToast } from 'src/context/ToastContext';
import { API_RESPONSE_CODE } from 'src/customer-call-sdk/config/constants';
import Connecting from '../Connecting';

const deleteVideomailButtonSx = {
    backgroundColor: '#FCEBEA',
    color: '#DF352C',
    borderRadius: '500px',
    fontWeight: 600,
    boxShadow: 'none',
    padding: '11px',
    width: '320px',
    textTransform: 'capitalize',
    fontSize: '20px',
    '&:hover': {
        backgroundColor: '#FEF4F3',
        boxShadow: 'none',
    },
    '&:focus': {
        outline: 'none',
    },
};

const deleteTextButtonStyle: React.CSSProperties = {
    color: '#DF352C',
};

function CustomerVideomail() {
    useCustomerCallRedirect();
    const { refreshedAccessToken } = useCall();
    const [videomails, setVideomails] = useState<VideoMail[]>([]);
    const [offset] = useState(0);
    const toast = useToast();
    const [limit] = useState(10);
    const [page] = useState(0);
    const [openVideoPlayer, setOpenVideoPlayer] = useState(false);
    const [videoToken, setVideoToken] = useState<string | null>(null);
    const [openVideomailDialog, setOpenVideomailDialog] = useState(false);
    const [selectedVideomail, setSelectedVideomail] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const fetchVideoMails = async () => {
        setLoading(true);
        const { videoMails } = await getAllVideoMail();
        if (videoMails) {
            setVideomails(videoMails);
        }
        setLoading(false);
    };

    const handleDeleteVideomail = async () => {
        if (!selectedVideomail) return;
        setLoading(true);
        const result = await deleteVideoMail(selectedVideomail);
        if (result.code === API_RESPONSE_CODE.SUCCESS) {
            await fetchVideoMails();
            toast.success(result.message || '');
        } else {
            toast.error(result.message || '');
        }
        setOpenVideomailDialog(false);
        setOpenVideoPlayer(false);
        setSelectedVideomail(null);
        setTimeout(() => {
            setLoading(false);
        }, 300);
    };

    const playVideo = (selectedVideoToken: string, videomailId: string) => {
        setVideoToken(selectedVideoToken);
        setSelectedVideomail(videomailId);
        setOpenVideoPlayer(true);
    };
    const closeVideoPlayer = () => {
        setVideoToken(null);
        setOpenVideoPlayer(false);
    };

    useEffect(() => {
        if (!refreshedAccessToken) {
            return;
        }
        fetchVideoMails();
    }, [page, limit, offset, refreshedAccessToken]);

    return (
        <UserLayout>
            <SEO
                title={VIDEO_MAIL_PAGE_TITLE}
                description={`${VIDEO_MAIL_PAGE_DESCRIPTION_PREFIX} - ${SITE_DESCRIPTION}`}
            />
            <Container>
                <PageTitle aria-label="Videomails">Videomails</PageTitle>
                <Paper>
                    {loading && (
                        <div style={{ height: '50px' }}>
                            <Connecting id="customer-videomail-loading" />
                        </div>
                    )}
                    {!loading && videomails?.length > 0 && (
                        <TableContainer id="customer-video-mail-table-container">
                            <Table
                                id="customer-video-mail-table"
                                aria-label="simple table"
                                size="medium"
                            >
                                <TableHead id="customer-video-mail-table-head">
                                    <TableRow id="customer-video-mail-table-row">
                                        <TableCell align="left">Caller Name</TableCell>
                                        <TableCell align="left">Caller Number</TableCell>
                                        <TableCell align="left">Status</TableCell>
                                        <TableCell align="left">Callee Number</TableCell>
                                        <TableCell align="left">Creadted On</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody id="customer-video-mail-table-body">
                                    {videomails.map((videoMail: VideoMail, index: number) => (
                                        <TableRow
                                            id={`customer-video-mail-table-row-${index}`}
                                            key={videoMail.id}
                                        >
                                            <TableCell align="left">{videoMail.name}</TableCell>
                                            <TableCell align="left">
                                                {videoMail.fromNumber}
                                            </TableCell>
                                            <TableCell align="left">
                                                {videoMail.status.charAt(0).toUpperCase() +
                                                    videoMail.status.slice(1)}
                                            </TableCell>
                                            <TableCell align="left">{videoMail.toNumber}</TableCell>
                                            <TableCell align="left" className="whitespace-no-wrap">
                                                {format(
                                                    new Date(videoMail.createdOn),
                                                    dateTimeFormat,
                                                )}
                                            </TableCell>
                                            <TableCell align="left">
                                                {videoMail.status === 'deleted' ? (
                                                    <Typography
                                                        style={{
                                                            fontWeight: 400,
                                                            marginLeft: '36px',
                                                            color: '#000',
                                                        }}
                                                    >
                                                        Unavailable
                                                    </Typography>
                                                ) : (
                                                    <Box display="flex">
                                                        <TextButton
                                                            onClick={() =>
                                                                playVideo(
                                                                    videoMail.videoToken,
                                                                    videoMail.id,
                                                                )
                                                            }
                                                            aria-label="View Video"
                                                        >
                                                            View
                                                        </TextButton>
                                                        <TextButton
                                                            style={deleteTextButtonStyle}
                                                            onClick={() => {
                                                                setSelectedVideomail(videoMail.id);
                                                                setOpenVideomailDialog(true);
                                                            }}
                                                        >
                                                            Delete
                                                        </TextButton>
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {!loading && videomails?.length === 0 && (
                        <div id="no-video-mails-div" className="flex p-4 justify-center mt-5">
                            No Videomails
                        </div>
                    )}
                </Paper>
                <Dialog
                    open={openVideoPlayer}
                    onClose={closeVideoPlayer}
                    fullWidth
                    maxWidth="md"
                    slotProps={{
                        paper: {
                            sx: {
                                minWidth: '600px',
                                minHeight: '600px',
                            },
                        },
                    }}
                >
                    <DialogTitle aria-label="Video Player">
                        Video Player
                        <IconButton
                            style={{ position: 'absolute', top: 8, right: 8 }}
                            onClick={closeVideoPlayer}
                            aria-label="Close Video Player Dialog"
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: '8px 160px',
                            flexDirection: 'column',
                        }}
                    >
                        <ZiggeoPlayer
                            apiKey={ZIGGEO_API_KEY}
                            video={videoToken}
                            theme="modern"
                            width="98%"
                            height="100%"
                            themecolor="blue"
                            skipinitial={false}
                            ziggeo-responsive
                        />
                    </DialogContent>
                    <DialogActions style={{ justifyContent: 'center', paddingBottom: '20px' }}>
                        <Button
                            variant="contained"
                            sx={deleteVideomailButtonSx}
                            onClick={() => {
                                setOpenVideomailDialog(true);
                            }}
                        >
                            Delete Videomail
                        </Button>
                    </DialogActions>
                </Dialog>

                <VideomailDialog
                    open={openVideomailDialog}
                    onClose={() => setOpenVideomailDialog(false)}
                    onDeleted={handleDeleteVideomail}
                    loading={loading}
                />
            </Container>
        </UserLayout>
    );
}
export default withAuth(CustomerVideomail);
